import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import { render, MARK_LINK } from 'storyblok-rich-text-react-renderer';
import { LinkWrapper } from '../../../utils/link';
import useHideOnSite from '../../../hooks/useLanguageFilter';
import CompassContext from './CompassContext';
import CompassControls from './CompassControls';
import CompassResortCard from './compass_resort';
import CompassPoll from './CompassPoll';
import { computeAgreement } from './computeAgreement';

const DEFAULT_LABELS = {
  resultsHeading: 'Din ortmatchning',
  next: 'Nästa',
  back: 'Tillbaka',
  submit: 'Se resultat',
  restart: 'Börja om',
  start: 'Starta',
  important: 'Extra viktigt',
  skip: 'Hoppa över',
  unskip: 'Ångra hoppa över',
  needAnswer: 'Svara på minst ett påstående för att se resultat.',
  pollHeading: 'Så här svarade andra',
  pollError: 'Kunde inte hämta omröstningen just nu.',
  pollEmpty: 'Du är först — fler resultat visas när fler svarat.',
  pollMatchHeading: 'Mest matchade orter',
  pollQuestionsHeading: 'Svar per påstående',
};

const renderRichText = (document) => {
  if (!document) {
    return null;
  }

  return render(document, {
    defaultBlokResolver: (name, props) => {
      const nested = { ...props, component: name };
      return <StoryblokComponent blok={nested} key={nested._uid} />;
    },
    markResolvers: {
      [MARK_LINK]: (children, props) => (
        <LinkWrapper link={props}>{children}</LinkWrapper>
      ),
    },
  });
};

const initialAnswersFromQuestions = (questions) => {
  const map = {};
  (questions || []).forEach((question) => {
    if (question?.question_key) {
      map[question.question_key] = {
        value: null,
        important: false,
        skipped: false,
      };
    }
  });
  return map;
};

const answersForScoring = (answersMap) =>
  Object.entries(answersMap || {}).map(([question_key, answer]) => ({
    question_key,
    value: answer.skipped ? null : answer.value,
    important: Boolean(answer.important),
  }));

const Compass = ({ blok }) => {
  const hideOnSite = useHideOnSite(blok.hide_on_site);

  const questions = blok?.questions || [];
  const resorts = blok?.resorts || [];
  const shouldShowIntro = Boolean(blok?.start_button_label);
  const [stage, setStage] = useState(shouldShowIntro ? 'intro' : 'question');
  const [questionIdx, setQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState(() =>
    initialAnswersFromQuestions(questions),
  );
  const [pollStats, setPollStats] = useState(null);
  const [pollError, setPollError] = useState(false);
  const submitStartedRef = useRef(false);

  const currentQuestion = questions[questionIdx] || null;
  const totalQuestions = questions.length;
  const isLastQuestion = questionIdx >= totalQuestions - 1;

  const { ranked, answeredCount } = useMemo(
    () =>
      computeAgreement(answersForScoring(answers), resorts, {
        warn:
          process.env.NODE_ENV !== 'production'
            ? (msg) => {
                // eslint-disable-next-line no-console
                console.warn(msg);
              }
            : undefined,
      }),
    [answers, resorts],
  );

  const resortByKey = useMemo(() => {
    const map = new Map();
    resorts.forEach((resort) => {
      if (resort?.resort_key) {
        map.set(resort.resort_key, resort);
      }
    });
    return map;
  }, [resorts]);

  const setAnswerImportant = (questionKey, important) => {
    setAnswers((prev) => ({
      ...prev,
      [questionKey]: {
        ...(prev[questionKey] || { value: null, skipped: false }),
        important,
      },
    }));
  };

  const setAnswerSkipped = (questionKey, skipped) => {
    setAnswers((prev) => ({
      ...prev,
      [questionKey]: {
        ...(prev[questionKey] || {}),
        skipped,
        value: skipped ? null : prev[questionKey]?.value ?? null,
        important: skipped ? false : prev[questionKey]?.important,
      },
    }));
  };

  const advanceAfterAnswer = () => {
    if (isLastQuestion) {
      setStage('result');
      return;
    }
    setQuestionIdx((idx) => idx + 1);
  };

  const selectAnswerAndAdvance = (questionKey, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionKey]: {
        ...(prev[questionKey] || {}),
        value,
        skipped: false,
      },
    }));
    advanceAfterAnswer();
  };

  const skipQuestionAndAdvance = (questionKey) => {
    setAnswers((prev) => ({
      ...prev,
      [questionKey]: {
        ...(prev[questionKey] || {}),
        skipped: true,
        value: null,
        important: false,
      },
    }));
    advanceAfterAnswer();
  };

  const restartCompass = () => {
    submitStartedRef.current = false;
    setPollStats(null);
    setPollError(false);
    setStage(shouldShowIntro ? 'intro' : 'question');
    setQuestionIdx(0);
    setAnswers(initialAnswersFromQuestions(questions));
  };

  useEffect(() => {
    if (stage !== 'result' || answeredCount < 1 || submitStartedRef.current) {
      return undefined;
    }

    const compassId = blok?.compass_id;
    const topResortKey = ranked[0]?.resortKey;
    if (!compassId || !topResortKey) {
      return undefined;
    }

    submitStartedRef.current = true;
    let cancelled = false;

    const run = async () => {
      try {
        const response = await fetch('/api/compass/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            compass_id: compassId,
            top_resort_key: topResortKey,
            answers: answersForScoring(answers),
          }),
        });
        if (!response.ok) {
          throw new Error(`Submit failed: ${response.status}`);
        }
        const stats = await response.json();
        if (!cancelled) {
          setPollStats(stats);
          setPollError(false);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setPollError(true);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [stage, answeredCount, ranked, answers, blok?.compass_id]);

  const labels = {
    next: blok?.next_button_label || DEFAULT_LABELS.next,
    back: blok?.back_button_label || DEFAULT_LABELS.back,
    submit: blok?.submit_button_label || DEFAULT_LABELS.submit,
    restart: blok?.restart_button_label || DEFAULT_LABELS.restart,
    start: blok?.start_button_label || DEFAULT_LABELS.start,
    important: blok?.important_label || DEFAULT_LABELS.important,
    skip: blok?.skip_label || DEFAULT_LABELS.skip,
    unskip: blok?.unskip_label || DEFAULT_LABELS.unskip,
    needAnswer: blok?.need_answer_message || DEFAULT_LABELS.needAnswer,
    pollHeading: blok?.poll_heading || DEFAULT_LABELS.pollHeading,
    pollError: blok?.poll_error_message || DEFAULT_LABELS.pollError,
    pollEmpty: blok?.poll_empty_message || DEFAULT_LABELS.pollEmpty,
    pollMatchHeading: blok?.poll_match_heading || DEFAULT_LABELS.pollMatchHeading,
    pollQuestionsHeading:
      blok?.poll_questions_heading || DEFAULT_LABELS.pollQuestionsHeading,
  };

  const contextValue = useMemo(
    () => ({
      stage,
      questionIdx,
      totalQuestions,
      currentQuestionUid: currentQuestion?._uid || null,
      answers,
      setAnswerImportant,
      setAnswerSkipped,
      selectAnswerAndAdvance,
      skipQuestionAndAdvance,
      isLastQuestion,
      goBack: () => {
        if (questionIdx > 0) {
          setQuestionIdx((idx) => idx - 1);
          return;
        }
        if (shouldShowIntro) {
          setStage('intro');
        }
      },
      startCompass: () => setStage('question'),
      restartCompass,
      hasIntroStage: shouldShowIntro,
      labels,
    }),
    [
      answers,
      currentQuestion,
      isLastQuestion,
      labels,
      questionIdx,
      shouldShowIntro,
      stage,
      totalQuestions,
    ],
  );

  if (hideOnSite) {
    return null;
  }

  return (
    <div
      className="w-full col-span-12 grid grid-cols-subgrid mt-8 gap-y-6 px-4 md:px-8"
      {...storyblokEditable(blok)}
    >
      <div className="col-span-12">
        {blok?.title ? <h2>{blok.title}</h2> : null}
        {stage === 'intro' ? (
          <div className="mt-2">{renderRichText(blok?.description)}</div>
        ) : null}
      </div>

      <CompassContext.Provider value={contextValue}>
        {stage === 'question' && currentQuestion ? (
          <>
            <div
              className="col-span-12"
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={Math.max(totalQuestions, 1)}
              aria-valuenow={Math.min(
                questionIdx + 1,
                Math.max(totalQuestions, 1),
              )}
              aria-label="Compass progress"
            >
              <div className="mb-2 text-sm">
                {Math.min(questionIdx + 1, totalQuestions)} / {totalQuestions}
              </div>
              <progress
                className="w-full h-1"
                max={Math.max(totalQuestions, 1)}
                value={Math.min(
                  questionIdx + 1,
                  Math.max(totalQuestions, 1),
                )}
              />
            </div>
            <StoryblokComponent blok={currentQuestion} />
          </>
        ) : null}

        {stage === 'result' ? (
          <div className="col-span-12 grid grid-cols-subgrid gap-y-4">
            <h3 className="col-span-12">
              {blok?.results_heading || DEFAULT_LABELS.resultsHeading}
            </h3>

            {answeredCount < 1 ? (
              <p className="col-span-12">{labels.needAnswer}</p>
            ) : (
              ranked.map((entry, index) => {
                const resort = resortByKey.get(entry.resortKey);
                if (!resort) {
                  return null;
                }
                return (
                  <CompassResortCard
                    key={resort._uid || entry.resortKey}
                    blok={resort}
                    percent={entry.percent}
                    isTop={index === 0}
                  />
                );
              })
            )}

            {answeredCount >= 1 && blok?.compass_id ? (
              <CompassPoll
                compassId={blok.compass_id}
                resorts={resorts}
                questions={questions}
                stats={pollStats}
                pollError={pollError}
                labels={labels}
              />
            ) : null}
          </div>
        ) : null}

        <CompassControls />
      </CompassContext.Provider>
    </div>
  );
};

export default Compass;
